import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import { Logo } from "../components/Logo";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pendingId = searchParams.get("pending");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const nextUrl = pendingId
        ? `/dashboard?activated=1`
        : "/dashboard";

    const activatePending = async () => {
        if (!pendingId) return null;
        try {
            const { data } = await api.post(`/preview/${encodeURIComponent(pendingId)}/activate`);
            return data;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not activate your preview link");
            return null;
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!agreed) {
            setError("Please accept the Terms of Service and Privacy Policy to continue.");
            return;
        }
        setLoading(true);
        setError("");
        const res = await register(email, password, name);
        if (res.ok) {
            const activated = await activatePending();
            setLoading(false);
            if (activated) {
                toast.success(`Activated /${activated.slug} — it's yours forever now.`);
            }
            navigate(nextUrl);
        } else {
            setLoading(false);
            setError(res.error);
        }
    };

    const googleLogin = () => {
        if (!agreed) {
            setError("Please accept the Terms of Service and Privacy Policy to continue with Google.");
            return;
        }
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + nextUrl + (pendingId ? `&pending=${encodeURIComponent(pendingId)}` : "");
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const socialpulseLogin = () => {
        if (!agreed) {
            setError("Please accept the Terms of Service and Privacy Policy to continue with SocialPulse.");
            return;
        }
        const backend = process.env.REACT_APP_BACKEND_URL;
        const params = new URLSearchParams();
        if (pendingId) params.set("pending", pendingId);
        params.set("next", nextUrl);
        window.location.href = `${backend}/api/auth/socialpulse/start?${params.toString()}`;
    };

    return (
        <div className="App min-h-screen">
            <Nav />
            <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl grid-cols-1 lg:grid-cols-2">
                <main className="order-2 flex items-center justify-center p-6 sm:p-12 lg:order-1">
                    <div className="w-full max-w-md">
                        <h1 className="font-display text-4xl font-bold tracking-tighter">Create account</h1>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Already have one?{" "}
                            <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="link-to-login">
                                Sign in
                            </Link>
                        </p>
                        {pendingId && (
                            <div className="mt-5 rounded-md border-2 border-[hsl(27,96%,55%)]/50 bg-[hsl(27,96%,55%)]/10 p-4 text-sm leading-relaxed text-foreground" data-testid="pending-notice">
                                <div className="flex items-start gap-2">
                                    <span className="text-lg leading-none">🎯</span>
                                    <div>
                                        <div className="font-semibold">
                                            After signup, we'll activate your preview short URL and QR code — the same slug you saw goes live under your account.
                                        </div>
                                        <div className="mt-2 text-xs text-muted-foreground">
                                            Preview reservations last <strong className="text-foreground">30 minutes</strong>. If yours has expired, just paste the URL again after signup.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Button
                            type="button"
                            variant="outline"
                            className="mt-8 h-11 w-full gap-3"
                            onClick={socialpulseLogin}
                            data-testid="socialpulse-register-btn"
                        >
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(27,96%,55%)] text-[10px] font-bold text-white">
                                SP
                            </span>
                            Continue with SocialPulse
                        </Button>

                        <Button
                            type="button"
                            variant="outline"
                            className="mt-3 h-11 w-full gap-3"
                            onClick={googleLogin}
                            data-testid="google-register-btn"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                            </svg>
                            Continue with Google
                        </Button>

                        <div className="relative my-8">
                            <Separator />
                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-xs uppercase tracking-widest text-muted-foreground">
                                or with email
                            </span>
                        </div>

                        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
                            <div>
                                <Label htmlFor="reg-name">Name</Label>
                                <Input id="reg-name" value={name} onChange={(e) => setName(e.target.value)}
                                    className="mt-2 h-11" data-testid="register-name-input" />
                            </div>
                            <div>
                                <Label htmlFor="reg-email">Email</Label>
                                <Input id="reg-email" type="email" required value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="mt-2 h-11" data-testid="register-email-input" />
                            </div>
                            <div>
                                <Label htmlFor="reg-pw">Password</Label>
                                <Input id="reg-pw" type="password" required value={password} minLength={6}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="mt-2 h-11" data-testid="register-password-input" />
                            </div>
                            {error && (
                                <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">
                                    {error}
                                </div>
                            )}
                            <div className="flex items-start gap-3 rounded-md border border-border bg-card p-3">
                                <Checkbox
                                    id="agree-terms"
                                    checked={agreed}
                                    onCheckedChange={(v) => setAgreed(!!v)}
                                    className="mt-0.5"
                                    data-testid="agree-terms-checkbox"
                                />
                                <label htmlFor="agree-terms" className="text-xs leading-relaxed text-muted-foreground">
                                    I agree to the{" "}
                                    <Link to="/terms" target="_blank" className="font-semibold text-primary hover:underline" data-testid="agree-terms-link">
                                        Terms of Service
                                    </Link>
                                    ,{" "}
                                    <Link to="/privacy" target="_blank" className="font-semibold text-primary hover:underline" data-testid="agree-privacy-link">
                                        Privacy Policy
                                    </Link>
                                    , and{" "}
                                    <Link to="/cookies" target="_blank" className="font-semibold text-primary hover:underline" data-testid="agree-cookies-link">
                                        Cookie Policy
                                    </Link>
                                    .
                                </label>
                            </div>
                            <Button type="submit" disabled={loading || !agreed} className="h-11 w-full bg-primary hover:bg-primary/90" data-testid="register-submit-btn">
                                {loading ? "Creating..." : "Create account"}
                            </Button>
                        </form>
                    </div>
                </main>

                <aside className="order-1 hidden border-l border-border bg-[hsl(224,15%,7%)] p-12 text-[hsl(210,20%,98%)] lg:order-2 lg:flex lg:flex-col lg:justify-between">
                    <Logo textClass="text-2xl text-white" />
                    <div>
                        <h2 className="font-display text-5xl font-extrabold leading-tight tracking-tighter">
                            Start<br />shortening.
                        </h2>
                        <p className="mt-4 max-w-md text-[hsl(215,20%,75%)]">
                            Free forever for personal use. QR codes, analytics, custom aliases —
                            all included.
                        </p>
                    </div>
                    <div className="font-mono text-xs text-[hsl(215,20%,55%)]">
                        © {new Date().getFullYear()} Splink.pro
                    </div>
                </aside>
            </div>
        </div>
    );
}
